import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import { BsArrowRight } from "react-icons/bs";
import { SEND_STOCK_SHIPMENT_TO_ODOO } from "./graphql";

const SyncStockShipmentToOdooView = ({ shipment, onCompleted }) => {
  const [confirmedCreateNewTransfer, setConfirmedCreateNewTransfer] =
    useState(false);
  const [sendStockShipmentToOdoo, sendStockShipmentToOdooRes] = useMutation(
    SEND_STOCK_SHIPMENT_TO_ODOO,
    {
      onCompleted(res) {
        const odooName =
          res?.sendStockShipmentToOdoo?.shipment?.odooStockPickingName;
        Alert(
          "success",
          odooName
            ? `Created Internal Transfer in Odoo: ${odooName}`
            : "Created Internal Transfer in Odoo.",
        );
        if (onCompleted) onCompleted(res);
      },
      onError(error) {
        Alert("error", parseError(error));
      },
    },
  );

  const lines = shipment.lines;
  const hasExistingOdooTransfer = Boolean(shipment.odooStockPickingId);
  const confirmCheckboxId = `confirm-create-new-transfer-${shipment.id}`;
  const totalQty = lines.reduce(
    (sum, line) => sum + Math.abs(Number(line.qty) || 0),
    0,
  );

  function createHandler() {
    sendStockShipmentToOdoo({
      variables: {
        shipmentId: shipment.id,
      },
    });
  }

  return (
    <div className="space-y-6 text-sm">
      <div className="card">
        <h5>Internal Transfer</h5>

        <div className="mt-4 -mx-2">
          <table className="whitespace-nowrap">
            <tbody>
              <tr>
                <td className="w-40">
                  <label>Date:</label>
                </td>
                <td className="w-full">{shipment.date}</td>
              </tr>
              <tr>
                <td>
                  <label>Operation Type:</label>
                </td>
                <td>Waboba - Netherlands: Internal Transfers</td>
              </tr>
              <tr>
                <td>
                  <label>Transfer:</label>
                </td>
                <td>
                  <div className="flex items-center space-x-2">
                    <span>
                      {shipment.stockCategory.name} (
                      {shipment.stockCategory.odooLocationName})
                    </span>
                    <BsArrowRight />
                    <span>
                      {shipment.toStockCategory.name} (
                      {shipment.toStockCategory.odooLocationName})
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div>
          <h5>Products</h5>
          <div className="mt-4 -mx-2">
            <table>
              <thead>
                <tr>
                  <th>Product ({lines.length})</th>
                  <th className="text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr
                    key={line.id || index}
                    className="border-y dark:border-gray-700"
                  >
                    <td>
                      {index + 1}. [{line.product.number}] {line.product.name}
                    </td>
                    <td className="text-right">
                      {Math.abs(Number(line.qty) || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="text-right">Total QTY</td>
                  <td className="text-right">{totalQty}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {hasExistingOdooTransfer ? (
        <div className="mt-3 leading-relaxed">
          <div className="text-orange-600 font-semibold">
            ⚠️ This record already has an Odoo transfer (
            {shipment.odooStockPickingName || shipment.odooStockPickingId}). You
            can still create a new one, make sure this is intended.
          </div>

          <div className="mt-3 flex items-center space-x-2">
            <input
              id={confirmCheckboxId}
              type="checkbox"
              checked={confirmedCreateNewTransfer}
              onChange={(e) => setConfirmedCreateNewTransfer(e.target.checked)}
            />
            <label htmlFor={confirmCheckboxId} className="opacity-100">
              I understand this shipment already has a transfer, and I still
              want to create a new one.
            </label>
          </div>

          <div className="text-xs opacity-70">
            Click the checkbox above to continue creating.
          </div>
        </div>
      ) : null}

      <div className="flex items-center">
        <Button
          primary
          onClick={createHandler}
          disabled={
            sendStockShipmentToOdooRes.loading ||
            (hasExistingOdooTransfer && !confirmedCreateNewTransfer)
          }
          loading={sendStockShipmentToOdooRes.loading}
        >
          Create Internal Transfer in Odoo
        </Button>
      </div>
    </div>
  );
};

export default SyncStockShipmentToOdooView;
